import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { SparklesIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(detail || err.message || 'Email ou senha incorretos');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner text="Conectando com o universo..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-mystical flex items-center justify-center px-4">
      {/* Efeitos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-48 bg-gradient-to-br from-mystical-600/20 to-mystical-800/20 rounded-lg shadow-2xl transform rotate-12 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-36 bg-gradient-to-br from-gold-600/20 to-gold-800/20 rounded-lg shadow-2xl transform -rotate-6 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-40 left-20 w-28 h-40 bg-gradient-to-br from-mystical-500/20 to-mystical-700/20 rounded-lg shadow-2xl transform rotate-6 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo e título */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-mystical-500 to-mystical-600 rounded-full mb-4 mystical-glow">
            <SparklesIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-mystical font-bold text-white mb-2">Bem-vindo de volta</h1>
          <p className="text-gray-200 font-elegant">Entre em sua conta para acessar suas consultas</p>
        </div>

        {/* Formulário */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo de email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-mystical-500 focus:border-transparent transition-all"
                placeholder="seu@email.com"
              />
            </div>

            {/* Campo de senha */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Senha
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-mystical-500 focus:border-transparent transition-all pr-12"
                  placeholder="Sua senha"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Mensagem de erro */}
            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm">
                {error}
              </div>
            )}

            {/* Botão de login */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-gradient-to-r from-mystical-500 to-mystical-600 text-white rounded-lg font-semibold hover:from-mystical-600 hover:to-mystical-700 focus:outline-none focus:ring-2 focus:ring-mystical-500 focus:ring-offset-2 focus:ring-offset-transparent transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          {/* Links adicionais */}
          <div className="mt-6 text-center space-y-2">
            <Link
              to="/forgot-password"
              className="block text-gold-400 hover:text-gold-300 text-sm transition-colors"
            >
              Esqueci minha senha
            </Link>
            <p className="text-gray-300 text-sm">
              Não tem uma conta?{' '}
              <Link
                to="/register"
                className="text-gold-400 hover:text-gold-300 font-semibold transition-colors"
              >
                Criar conta
              </Link>
            </p>
            <Link
              to="/"
              className="inline-block text-gray-400 hover:text-white text-sm transition-colors"
            >
              ← Voltar ao início
            </Link>
          </div>
        </div>

        {/* Informações adicionais */}
        <div className="mt-8 text-center">
          <p className="text-gray-300 text-sm">
            🔮 Suas consultas são privadas e seguras
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;